<template>
  <div id="wizard" :class="{'wizard-fullheight': isLoadingScreen}">
    <component
      ref="comp"
      v-for="(item, key) in steps" :key="key"
      :class="{'active' : key === currStep, 'prev': key < currStep, 'next' : key > currStep, 'disabled': key > currStep && !isCurrStepValid}"
      :is="item.component"
      :item="item"/>
    <button ref="nextStepBtn" class="next-step-btn" @click="nextStep" :class="{'hide': transition}"
      :disabled="!isCurrStepValid" v-if="!isLoadingScreen">
      <svg xmlns="http://www.w3.org/2000/svg" width="25.805" height="44.891" viewBox="0 0 25.805 44.891">
        <path d="M8806.227,558.43l8.188-8.189c10.092-10.091,10.926-11.013,10.926-11.013s-.834-.788-10.976-10.93l-8.239-8.239,1.264-1.264c.509-.508.949-.946,1.316-1.307,1.9,1.881,5.82,5.782,10.946,10.908l10.864,10.865-10.831,10.832c-5.109,5.109-9.016,9-10.912,10.875C8808.4,560.6,8806.227,558.43,8806.227,558.43Z" transform="translate(-8805.419 -516.783)" fill="#fff" stroke="#fff" stroke-miterlimit="10" stroke-width="1"/>
      </svg>
    </button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mapActions } from 'vuex'
import BrandName from '@/components/Wizard/BrandName.vue'
import BrandKeywords from '@/components/Wizard/BrandKeywords.vue'
import BrandColors from '@/components/Wizard/BrandColors.vue'
import BrandEnd from '@/components/Wizard/BrandEnd.vue'
import { setTimeout } from 'timers';

export default {
  name: 'Wizard',
  data() {
    return {
      steps: [{
        component: BrandName,
      }, {
        component: BrandKeywords,
      }, {
        component: BrandColors,
      }, {
        component: BrandEnd,
      }],
      transition: false,
    }
  },
  computed: {
    ...mapGetters('wizard', [
      'currStep',
      'isCurrStepValid',
    ]),
    isLoadingScreen() {
      return this.currStep === this.steps.length - 1;
    }
  },
  methods: {
    ...mapActions('wizard', [
      'nextStep'
    ])
  },
  watch: {
    currStep() {
      this.transition = true;
      if(this.currStep !== this.steps.length - 1) {
        setTimeout(() => {
          this.transition = false;
        }, 2000)
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/scss/variables.scss';

/*body bg*/
body {
  background-color: #EAEAE9;
}

/*Reset button styles*/
button {
  border: none;
  margin: 0;
  padding: 0;
  width: auto;
  overflow: visible;
  background: transparent;
  color: inherit;
  font: inherit;
  outline: none;
  line-height: normal;
  -webkit-appearance: none;
  cursor: pointer;

  &:hover {
    opacity: .7;
  }
}

button::-moz-focus-inner {
  border: none;
}

button:disabled {
  cursor: not-allowed;
  pointer-events: all !important;
}

/*Button styles*/
.btn {
  display: inline-block;
  text-align: center;
  text-decoration: none;
  margin: 2px 0;
  border: solid 1px transparent;
  border-radius: 1em;
  padding: 0.25em 1em;
  color: $color_white;
  background-color: $bluey-grey;

  &.pale-teal {
    background-color: $pale-teal-dark;
  }

  &.btn-big {
    padding: .75em 1.5em;
    border-radius: 2em;
  }
}

/*Reset input styles*/
input {
	margin: 0;
	border: 0;
	padding: 0;
	display: inline-block;
	vertical-align: middle;
	white-space: normal;
	background: none;
	line-height: 1;
	font-size: inherit;
  font-family:inherit;
}

input:focus {
	outline: 0;
}

/*SVG nest currentColor*/
svg {
  fill: currentColor;
}

/*Component styles*/
#wizard {
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 100vh;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: 0 1px 0 $grey;
}

.wizard-panel {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  transition: transform .35s cubic-bezier(0.77, 0, 0.175, 1);
  overflow-x: hidden;
  overflow-y: scroll;

  &.next {
    transform: translateX(105%);

    &.disabled {
      .wizard-card:nth-child(1) {
        background-color: $very-light-pink !important;
      }
    }
  }

  &.active {
    transform: translateX(0);
  }

  &.prev {
    transform: translateX(-105%);
  }

  .wizard-card {
    height: 100%;
    padding: 2em;
    background: $color_white;

    h2 {
      font-size: 1.375em;
      letter-spacing: 3px;
      line-height: 1.41;
      text-transform: uppercase;
    }

    p {
      line-height: 1.55;
    }
  }

  .wizard-card:nth-child(1) {
    height: auto;
    color: $color_white;
    max-height: 17em;
  }
}

/*Next step button*/
.next-step-btn {
  display: none;
  position: absolute;
  font-size: 4em;
  width: 12%;
  height: 100%;
  right: 0;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  transition: opacity 250ms ease-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }

  &:active,
  &:focus {
    background-color: transparent;
  }

  &.hide {
    opacity: 0;
    transition-delay: 350ms;

     &:hover {
      background-color: transparent;
    }
  }

  & > svg {
    position: relative;
    left: 7%;
  }
}

/*Next step big btn*/
.wizard-card-actions {
  position: absolute;
  bottom: 3em;
  right: 5em;
  display: flex;

  .next-step-big-btn {
    margin-left: auto;
    display: flex;
  }
}

/*Prev step buttn*/
.prev-step-btn {
  position: absolute;
  bottom: 3em;
  display: none;
}

@media (min-aspect-ratio: 9/16) and (orientation: landscape) and (max-height: 576px),
  (orientation: portrait) and (max-width: 576px) {

  .wizard-card-actions {
    margin-top: 2em;
    position: static;
  }
}

@media (min-width: 576px), (orientation: landscape) {
  #wizard {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 786px;
    max-height: 575px;
    transition: all .65s cubic-bezier(0.77, 0, 0.175, 1);

    &.wizard-fullheight {
      max-height: 100%;
      max-width: 100%;
      transition-delay: 1s;
    }
  }

  .wizard-panel {
    flex-direction: row;
    transition: transform .65s cubic-bezier(0.77, 0, 0.175, 1);
    overflow-x: hidden;
    overflow-y: hidden;

    &.next {
      transform: translateX(0);
    }

    .wizard-card {
      width: 54%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
      transition: transform .65s cubic-bezier(0.645, 0.045, 0.355, 1), background-color .35s  cubic-bezier(0.77, 0, 0.175, 1);
      overflow-x: hidden;
      overflow-y: scroll;
    }

    .wizard-card:nth-child(1) {
      position: absolute;
      width: 100%;
      height: 100%;
      max-height: 100%;
      padding-left: calc(55% + 2em);
      z-index: 2;
    }

    .wizard-card:nth-child(2) {
      transform: translateX(90%);
      z-index: -1;

      .wizard-card-content {
        width: 100%;
      }
    }

    &.active .wizard-card:nth-child(1) {
      transform: translateX(-50%);
    }

    &.prev .wizard-card:nth-child(1) {
      transform: translateX(-10%);
    }

    &.active .wizard-card:nth-child(1) {
      transition-delay: .45s;
    }
    &.active .wizard-card:nth-child(2) {
      transition-delay: .35s;
    }

    &.next .wizard-card:nth-child(2) {
      transform: translateX(150%);
    }

    // Opacity of content
    .wizard-card .wizard-card-content {
      opacity: 0;
      transition: opacity .350s cubic-bezier(0.645, 0.045, 0.355, 1);
      transition-delay: 0s;
    }

    &.active .wizard-card .wizard-card-content {
      opacity: 1;
      transition-duration: 1s;
      transition-delay: .65s;
    }
  }

  .prev-step-btn {
    display: block;
  }

  .wizard-card-actions {

    .prev-step-big-btn {
      display: none;
    }
  }
}

@media (min-width: 768px) {
  .wizard-panel {

    .wizard-card {
      width: 45%;
      overflow-x: hidden;
      overflow-y: hidden;
    }

    .wizard-card:nth-child(2) {
      transform: translateX(98%);
      z-index: -1;
    }

    &.active .wizard-card:nth-child(1) {
      transform: translateX(-55%);
      padding-left: calc(55% + 5em);
    }
  }

  .next-step-btn {
    display: flex;
  }

  .wizard-card-actions {
    display: none;
  }
}

@media (min-width: 992px) {
  #wizard {
    max-width: 940px;
  }

  .wizard-panel {

    .wizard-card {
      padding: 2em 5em;
    }

    .wizard-card:nth-child(1) {
      padding-left: calc(55% + 5em);
    }
  }
}

@media (min-width: 1200px) {
  #wizard {
    max-width: 1100px;
  }
}

/*Input styles*/
.input {
  color: $dusk;
  font-size: 1.125em;
  line-height: 3em;
  padding: 0 1em;
  width: 100%;
  border-radius: 4px;
  border: solid 1px $pale-blue;
  background-color: $color_white;
  box-shadow: 0 1px $pale-blue;

  &:not(:last-child) {
    margin-bottom: 2em;
  }

  &::placeholder {
    color: $light-grey;
  }

  &:active,
  &:focus {
    box-shadow: 0 1px $steel;
    border-color: $cloudy-blue;
  }
}

#wizard .vue-input-tag-wrapper {
  padding: 1em 1em 2em;
  width: 100%;
  border-radius: 4px;
  border: solid 1px $pale-blue;
  margin-bottom: 2em;

  &:active,
  &:focus,
  &:focus-within {
    box-shadow: 0 1px $steel;
    border-color: $cloudy-blue;
  }

  .input-tag {
    background-color: $cloudy-blue;
    border: none;
    color: $color_white;
    padding: 0.15em 1em 0;
    font-size: 1em;
    border-radius: 2em;
    box-shadow: 0 1px $bluey-grey;
    margin-bottom: 8px;
    margin-right: 8px;

    .remove {
      color: $color_white;
      margin-left: .5em;

      &:empty::before {
        content: '×';
        font-size: 1.75em;
        vertical-align: middle;
      }
    }
  }

  .new-tag {
    font-size: 1em;
    color: $dusk;
  }
}
</style>
